import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { UserProfileDto } from "@/shared/dtos/user-profile-dto";
import { MenuOptionsService } from "@/shared/services/menuOptionsService";
import { UserService } from "@/shared/services/userService";
import { setUserPanel } from "@/store/userSlice/userSlice";
import Button from "devextreme-react/button";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ItiLanguageSelector from "../../../iti-core-react/components/ItiLanguageSelector/ItiLanguageSelector";
import DarkModeToggle from "../DarkModeToggle/DarkModeToggle";
import NotificationPanel from "../NotificationsPanel/NotificationsPanelTest";
import UserPanel from "../UserPanel/UserPanel";
import "./Header.scss";

interface IHeaderProps {
	menuToggleEnabled: boolean;
	title: string;
	className?: string;
	toggleMenu?: (e: any) => void;
	showNavigation?: boolean;
	children?: any;
	dark?: boolean;
	showBackButton?: boolean;
	onBackButtonClicked?: () => void;
}

export default function Header({
	menuToggleEnabled,
	title,
	toggleMenu,
	showNavigation = false,
	children,
	dark,
	className,
	showBackButton = false,
	onBackButtonClicked,
}: Readonly<IHeaderProps>) {
	const { t } = useLanguage();
	const dispatch = useDispatch();
	const [userProfile, setUserProfile] = useState<UserProfileDto>(
		new UserProfileDto(),
	);
	const [userMenuOptions, setUserMenuOptions] = useState<any[]>([]);
	const dataLoaded = useRef(false);
	const isLoading = useRef(false);

	async function loadData() {
		if (dataLoaded.current || isLoading.current) return;

		try {
			isLoading.current = true;

			const profileData = window.localStorage.getItem("profile");
			if (profileData) {
				const { userId } = JSON.parse(profileData);
				if (userId) {
					const userRes = await UserService.getUser(userId);
					setUserProfile(userRes);
				}
			}

			const menuRes = await MenuOptionsService.getUserMenu();

			if (menuRes) {
				const serializedMenu = menuRes.map((item) => ({
					id: item.id,
					text: item.text,
					i18n: item.i18n,
					link: item.link,
					href: item.href,
					target: item.target,
					icon: item.icon,
					items: item.items?.map((subItem) => ({
						id: subItem.id,
						text: subItem.text,
						i18n: subItem.i18n,
						link: subItem.link,
						href: subItem.href,
						target: subItem.target,
						icon: subItem.icon,
					})),
					contextItems: item.contextItems?.map((contextItem) => ({
						id: contextItem.id,
						text: contextItem.text,
						i18n: contextItem.i18n,
						link: contextItem.link,
						href: contextItem.href,
						target: contextItem.target,
						icon: contextItem.icon,
					})),
				}));

				setUserMenuOptions([...menuRes]);
				dispatch(setUserPanel(serializedMenu));
			}

			dataLoaded.current = true;
		} catch (error) {
			dataLoaded.current = false;
		} finally {
			isLoading.current = false;
		}
	}

	useEffect(() => {
		loadData();
	}, []);

	const toolbarClass = () => {
		return dark
			? `header-toolbar dark ${className ?? ""}`
			: `header-toolbar ${className ?? ""}`;
	};

	const backButtonOptions = {
		icon: "back",
		text: t("back"),
		stylingMode: "contained",
		type: "default",
		elementAttr: { class: "back-button" },
		visible: showBackButton,
		onClick: () => {
			if (onBackButtonClicked) {
				onBackButtonClicked();
			}
		},
	};

	const renderLanguageMenu = () => (
		<div className="language-selector-container">
			<ItiLanguageSelector />
		</div>
	);

	const renderUserButton = () => (
		<Button
			className="user-button authorization"
			height="100%"
			stylingMode="text"
		>
			<UserPanel
				menuMode="context"
				userProfile={userProfile}
				menuOptions={userMenuOptions}
			/>
		</Button>
	);

	const renderUserMenuContent = () => (
		<div className="user-panel-container">
			<UserPanel
				menuMode="list"
				userProfile={userProfile}
				menuOptions={userMenuOptions}
			/>
		</div>
	);

	return (
		<header className="header-component">
			<Toolbar className={toolbarClass()}>
				{menuToggleEnabled && (
					<Item location="before" widget="dxButton" cssClass="menu-button">
						<Button icon="menu" stylingMode="text" onClick={toggleMenu} />
					</Item>
				)}

				<Item location="before">
					<span className="welcome-texts welcome-texts-header">
						{t("login.title-app-1")} {t("login.title-app-2")}{" "}
						<span className="gradient-text">{t("login.title-app-3")}</span>
					</span>
				</Item>

				<Item location="before" widget="dxButton" options={backButtonOptions} />

				{title && (
					<Item
						location="before"
						cssClass={
							showNavigation
								? "header-logo-without-burger-img"
								: "header-logo-img"
						}
						text={title}
					/>
				)}

				<Item location="after" locateInMenu="auto">
					<NotificationPanel />
				</Item>

				<Item
					location="after"
					locateInMenu="auto"
					render={renderLanguageMenu}
					menuItemRender={renderLanguageMenu}
				>
					<ItiLanguageSelector />
				</Item>

				<Item location="after" locateInMenu="auto">
					<DarkModeToggle />
				</Item>

				<Item
					location="after"
					locateInMenu="auto"
					menuItemRender={renderUserMenuContent}
				>
					{renderUserButton()}
				</Item>
			</Toolbar>

			{children}
		</header>
	);
}
